import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { ArrowLeft, LogOut, Settings, Cloud, Mail, CheckCircle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ImpostazioniPage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [testingOnedrive, setTestingOnedrive] = useState(false);
  
  const [onedriveSettings, setOnedriveSettings] = useState({
    enabled: false,
    client_id: '',
    client_secret: '',
    tenant_id: '',
    redirect_uri: '',
    folder_path: '/PaMa',
    target_user_email: '',
    use_sharepoint: false,
    sharepoint_hostname: '',
    sharepoint_site_name: ''
  });

  const [emailSettings, setEmailSettings] = useState({
    enabled: false,
    smtp_server: '',
    smtp_port: 587,
    smtp_user: '',
    smtp_password: '',
    from_email: '',
    from_name: 'PaMa'
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      headers: {
        Authorization: `Bearer ${token}`
      }
    };
  };

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/settings`, getAuthHeaders());
      setOnedriveSettings(response.data.onedrive);
      setEmailSettings(response.data.email);
    } catch (error) {
      console.error('Errore caricamento impostazioni:', error);
      if (error.response?.status === 403) {
        toast.error('Non hai i permessi per accedere a questa sezione.');
        navigate('/');
      } else {
        toast.error('Errore nel caricamento delle impostazioni');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      await axios.put(`${API}/settings`, {
        onedrive: onedriveSettings,
        email: emailSettings
      }, getAuthHeaders());
      
      toast.success('Impostazioni salvate con successo');
    } catch (error) {
      console.error('Errore salvataggio impostazioni:', error);
      toast.error('Errore nel salvataggio delle impostazioni');
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    try {
      setTestingEmail(true);
      const response = await axios.post(`${API}/settings/test-email`, {}, getAuthHeaders());
      toast.success(response.data.message);
    } catch (error) {
      console.error('Errore test email:', error);
      toast.error(error.response?.data?.detail || 'Errore nel test email');
    } finally {
      setTestingEmail(false);
    }
  };

  const handleTestOnedrive = async () => {
    try {
      setTestingOnedrive(true);
      const response = await axios.post(`${API}/settings/test-onedrive`, {}, getAuthHeaders());
      toast.success(response.data.message);
    } catch (error) {
      console.error('Errore test OneDrive:', error);
      toast.error(error.response?.data?.detail || 'Errore nel test OneDrive');
    } finally {
      setTestingOnedrive(false);
    }
  };

  const [syncingOnedrive, setSyncingOnedrive] = useState(false);
  
  const handleSyncOnedrive = async () => {
    if (!window.confirm('Vuoi sincronizzare tutti gli allegati esistenti con OneDrive/SharePoint?\n\nQuesta operazione potrebbe richiedere diversi minuti.')) {
      return;
    }
    
    try {
      setSyncingOnedrive(true);
      toast.info('Sincronizzazione in corso... Attendere.');
      
      const response = await axios.post(`${API}/settings/sync-onedrive`, {}, getAuthHeaders());
      
      const { dettagli } = response.data;
      const totaleSync = dettagli.ordini_lavoro.sincronizzati + dettagli.progetti.sincronizzati + dettagli.cantieri.sincronizzati;
      const totaleFile = dettagli.ordini_lavoro.totale + dettagli.progetti.totale + dettagli.cantieri.totale;
      const totaleErrori = (dettagli.ordini_lavoro.errori || 0) + (dettagli.progetti.errori || 0) + (dettagli.cantieri.errori || 0);
      
      if (totaleSync === 0 && totaleFile > 0 && totaleErrori === 0) {
        toast.success(
          `Tutti i ${totaleFile} file sono già sincronizzati su OneDrive!\n` +
          `Ordini: ${dettagli.ordini_lavoro.totale} | Progetti: ${dettagli.progetti.totale} | Cantieri: ${dettagli.cantieri.totale}`
        );
      } else {
        toast.success(
          `Sincronizzazione completata!\n` +
          `Nuovi file caricati: ${totaleSync}\n` +
          `Ordini: ${dettagli.ordini_lavoro.sincronizzati}/${dettagli.ordini_lavoro.totale} | ` +
          `Progetti: ${dettagli.progetti.sincronizzati}/${dettagli.progetti.totale} | ` +
          `Cantieri: ${dettagli.cantieri.sincronizzati}/${dettagli.cantieri.totale}` +
          (totaleErrori > 0 ? `\n⚠️ Errori: ${totaleErrori}` : '')
        );
      }
    } catch (error) {
      console.error('Errore sincronizzazione OneDrive:', error);
      toast.error(error.response?.data?.detail || 'Errore nella sincronizzazione');
    } finally {
      setSyncingOnedrive(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-purple-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-3 py-3 sm:px-6 sm:py-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/')}
                className="border-slate-300"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
              <div className="flex flex-wrap items-center gap-3">
                <div className="bg-purple-600 p-2 rounded-lg">
                  <Settings className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Impostazioni</h1>
                  <p className="text-sm text-slate-600">Configura integrazioni e parametri sistema</p>
                </div>
              </div>
            </div>
            
            <Button
              variant="outline"
              onClick={onLogout}
              className="border-slate-300"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Esci
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-3 py-4 sm:px-6 sm:py-8">
        {loading ? (
          <div className="bg-white rounded-xl shadow-md border border-slate-200 p-8 text-center">
            Caricamento impostazioni...
          </div>
        ) : (
          <div className="space-y-6">
            {/* OneDrive Settings */}
            <div className="bg-white rounded-xl shadow-md border border-slate-200">
              <div className="p-6 border-b border-slate-200">
                <div className="flex flex-wrap items-center gap-3">
                  <Cloud className="w-6 h-6 text-blue-600" />
                  <div>
                    <h2 className="text-xl font-bold text-slate-900">Integrazione OneDrive</h2>
                    <p className="text-sm text-slate-600">Configura l'archiviazione cloud per i file di PaMa</p>
                  </div>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Enable OneDrive */}
                <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <input
                    type="checkbox"
                    id="onedrive_enabled"
                    checked={onedriveSettings.enabled}
                    onChange={(e) => setOnedriveSettings({ ...onedriveSettings, enabled: e.target.checked })}
                    className="w-5 h-5 text-blue-600 border-blue-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="onedrive_enabled" className="font-semibold text-blue-900 cursor-pointer">
                    Abilita archiviazione OneDrive
                  </label>
                </div>

                {onedriveSettings.enabled && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="onedrive_client_id">Client ID *</Label>
                        <Input
                          id="onedrive_client_id"
                          value={onedriveSettings.client_id}
                          onChange={(e) => setOnedriveSettings({ ...onedriveSettings, client_id: e.target.value })}
                          placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="onedrive_tenant_id">Tenant ID *</Label>
                        <Input
                          id="onedrive_tenant_id"
                          value={onedriveSettings.tenant_id}
                          onChange={(e) => setOnedriveSettings({ ...onedriveSettings, tenant_id: e.target.value })}
                          placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                          className="mt-1"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="onedrive_client_secret">Client Secret *</Label>
                      <Input
                        id="onedrive_client_secret"
                        type="password"
                        value={onedriveSettings.client_secret}
                        onChange={(e) => setOnedriveSettings({ ...onedriveSettings, client_secret: e.target.value })}
                        placeholder="••••••••••••••••"
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label htmlFor="onedrive_redirect_uri">Redirect URI *</Label>
                      <Input
                        id="onedrive_redirect_uri"
                        value={onedriveSettings.redirect_uri}
                        onChange={(e) => setOnedriveSettings({ ...onedriveSettings, redirect_uri: e.target.value })}
                        placeholder="https://your-app.com/auth/callback"
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label htmlFor="onedrive_folder_path">Cartella OneDrive</Label>
                      <Input
                        id="onedrive_folder_path"
                        value={onedriveSettings.folder_path}
                        onChange={(e) => setOnedriveSettings({ ...onedriveSettings, folder_path: e.target.value })}
                        placeholder="/PaMa"
                        className="mt-1"
                      />
                      <p className="text-xs text-slate-500 mt-1">Percorso della cartella root in OneDrive dove saranno salvati i file</p>
                    </div>

                    <div>
                      <Label htmlFor="onedrive_target_user">Email Utente OneDrive (Business)</Label>
                      <Input
                        id="onedrive_target_user"
                        type="email"
                        value={onedriveSettings.target_user_email}
                        onChange={(e) => setOnedriveSettings({ ...onedriveSettings, target_user_email: e.target.value })}
                        placeholder="nome.cognome@azienda.com"
                        className="mt-1"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        Email dell'account aziendale (solo per OneDrive Business, non SharePoint)
                      </p>
                    </div>

                    {/* SharePoint Toggle */}
                    <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <Label className="text-purple-900 font-semibold">Usa SharePoint</Label>
                          <p className="text-xs text-purple-700">Salva i file su SharePoint aziendale invece di OneDrive</p>
                        </div>
                        <Switch
                          checked={onedriveSettings.use_sharepoint}
                          onCheckedChange={(checked) => setOnedriveSettings({ ...onedriveSettings, use_sharepoint: checked })}
                        />
                      </div>
                      
                      {onedriveSettings.use_sharepoint && (
                        <div className="space-y-3 pt-3 border-t border-purple-200">
                          <div>
                            <Label htmlFor="sharepoint_hostname">SharePoint Hostname</Label>
                            <Input
                              id="sharepoint_hostname"
                              value={onedriveSettings.sharepoint_hostname}
                              onChange={(e) => setOnedriveSettings({ ...onedriveSettings, sharepoint_hostname: e.target.value })}
                              placeholder="mainsol.sharepoint.com"
                              className="mt-1"
                            />
                            <p className="text-xs text-slate-500 mt-1">Solo il dominio, senza https://</p>
                          </div>
                          <div>
                            <Label htmlFor="sharepoint_site_name">Nome Sito (opzionale)</Label>
                            <Input
                              id="sharepoint_site_name"
                              value={onedriveSettings.sharepoint_site_name}
                              onChange={(e) => setOnedriveSettings({ ...onedriveSettings, sharepoint_site_name: e.target.value })}
                              placeholder="PaMa"
                              className="mt-1"
                            />
                            <p className="text-xs text-slate-500 mt-1">Lascia vuoto per usare il sito root</p>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm text-blue-900 font-semibold mb-2">📘 Permessi API necessari in Azure:</p>
                      <ol className="text-xs text-blue-800 space-y-1 list-decimal list-inside">
                        <li>Files.ReadWrite.All (per OneDrive)</li>
                        <li>Sites.ReadWrite.All (per SharePoint)</li>
                        <li>Ricorda di cliccare "Concedi consenso amministratore"</li>
                      </ol>
                    </div>

                    {/* Test OneDrive Button + Sync Button */}
                    <div className="flex justify-between items-center pt-4 border-t border-slate-200">
                      <Button
                        onClick={handleSyncOnedrive}
                        disabled={syncingOnedrive || !onedriveSettings.enabled || !onedriveSettings.client_id}
                        variant="outline"
                        className="border-green-300 text-green-700 hover:bg-green-50"
                      >
                        {syncingOnedrive ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600 mr-2"></div>
                            Sincronizzazione in corso...
                          </>
                        ) : (
                          <>
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Sincronizza Tutti gli Allegati
                          </>
                        )}
                      </Button>
                      
                      <Button
                        onClick={handleTestOnedrive}
                        disabled={testingOnedrive || !onedriveSettings.client_id || !onedriveSettings.client_secret}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        {testingOnedrive ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Test in corso...
                          </>
                        ) : (
                          <>
                            <Cloud className="w-4 h-4 mr-2" />
                            Testa Connessione {onedriveSettings.use_sharepoint ? 'SharePoint' : 'OneDrive'}
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Email Settings */}
            <div className="bg-white rounded-xl shadow-md border border-slate-200">
              <div className="p-6 border-b border-slate-200">
                <div className="flex items-center gap-3">
                  <Mail className="w-6 h-6 text-green-600" />
                  <div>
                    <h2 className="text-xl font-bold text-slate-900">Configurazione Email</h2>
                    <p className="text-sm text-slate-600">Configura SMTP per invio email (reset password, notifiche)</p>
                  </div>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Enable Email */}
                <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg border border-green-200">
                  <input
                    type="checkbox"
                    id="email_enabled"
                    checked={emailSettings.enabled}
                    onChange={(e) => setEmailSettings({ ...emailSettings, enabled: e.target.checked })}
                    className="w-5 h-5 text-green-600 border-green-300 rounded focus:ring-green-500"
                  />
                  <label htmlFor="email_enabled" className="font-semibold text-green-900 cursor-pointer">
                    Abilita invio email
                  </label>
                </div>

                {emailSettings.enabled && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="smtp_server">Server SMTP *</Label>
                        <Input
                          id="smtp_server"
                          value={emailSettings.smtp_server}
                          onChange={(e) => setEmailSettings({ ...emailSettings, smtp_server: e.target.value })}
                          placeholder="smtp.gmail.com"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="smtp_port">Porta SMTP *</Label>
                        <Input
                          id="smtp_port"
                          type="number"
                          value={emailSettings.smtp_port}
                          onChange={(e) => setEmailSettings({ ...emailSettings, smtp_port: parseInt(e.target.value) })}
                          placeholder="587"
                          className="mt-1"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="smtp_user">Username SMTP *</Label>
                      <Input
                        id="smtp_user"
                        value={emailSettings.smtp_user}
                        onChange={(e) => setEmailSettings({ ...emailSettings, smtp_user: e.target.value })}
                        placeholder="user@example.com"
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label htmlFor="smtp_password">Password SMTP *</Label>
                      <Input
                        id="smtp_password"
                        type="password"
                        value={emailSettings.smtp_password}
                        onChange={(e) => setEmailSettings({ ...emailSettings, smtp_password: e.target.value })}
                        placeholder="••••••••••••••••"
                        className="mt-1"
                      />
                      <p className="text-xs text-slate-500 mt-1">Per Gmail usa una "App Password" invece della password normale</p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="from_email">Email Mittente *</Label>
                        <Input
                          id="from_email"
                          type="email"
                          value={emailSettings.from_email}
                          onChange={(e) => setEmailSettings({ ...emailSettings, from_email: e.target.value })}
                          placeholder="noreply@example.com"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="from_name">Nome Mittente</Label>
                        <Input
                          id="from_name"
                          value={emailSettings.from_name}
                          onChange={(e) => setEmailSettings({ ...emailSettings, from_name: e.target.value })}
                          placeholder="PaMa"
                          className="mt-1"
                        />
                      </div>
                    </div>

                    {/* Test Email Button */}
                    <div className="flex items-center gap-3">
                      <Button
                        onClick={handleTestEmail}
                        disabled={testingEmail}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        {testingEmail ? 'Invio in corso...' : 'Invia Email di Test'}
                      </Button>
                      <p className="text-xs text-slate-500">Invia un'email di prova al tuo indirizzo per verificare la configurazione</p>
                    </div>

                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-sm text-green-900 font-semibold mb-2">📧 Configurazione Gmail:</p>
                      <ol className="text-xs text-green-800 space-y-1 list-decimal list-inside">
                        <li>Server SMTP: smtp.gmail.com</li>
                        <li>Porta: 587</li>
                        <li>Vai su Google Account → Security → 2-Step Verification</li>
                        <li>In fondo, clicca su "App passwords"</li>
                        <li>Genera una password per "Mail" su "Other device"</li>
                        <li>Usa questa password invece della password Gmail normale</li>
                      </ol>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end">
              <Button
                onClick={handleSaveSettings}
                disabled={saving}
                className="bg-purple-600 hover:bg-purple-700 px-8"
                size="lg"
              >
                {saving ? 'Salvataggio...' : 'Salva Impostazioni'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImpostazioniPage;
